import { Box, Dialog, Typography, AppBar } from '@mui/material';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { faBars, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
// Components
import TwoFactorModal from '@/components/TwoFactorModal/TwoFactorModal';
// Hooks
import { useAuth } from '@/hooks/useAuth';

// Styles
import styles from './layout.module.css';
// Utils
import ModalBody from '@/common/ModalBody/ModalBody';
import { routes } from '@/routes/routes';
import Sidebar from './Sidebar/Sidebar';
import PermissionPlaceholder from '../CustomerTools/PermissionPlaceholder';
import { useSystemContext } from '@/providers/SystemProvider';

import { colors } from '@/theme/colors';

export function SupportLayout({ children, permission }) {
  const { status, authStatuses, userHasPermissions, memberData } = useAuth();
  const hasPermission = userHasPermissions(permission?.permission);
  const { config } = useSystemContext();
  const router = useRouter();
  const { user, isIdleModalShowing, setIsIdleModalShowing, setShow2fa } = useAuth();

  //For Support Layout, Deactivate 2FA Temporally
  const show2fa = false;

  const handleSessionModalClose = useCallback(() => {
    setIsIdleModalShowing(false);
  }, [setIsIdleModalShowing]);

  const handleEnroll = () => {
    setShow2fa(false);
    router.replace(routes.manageAccountTwoFactorAuth.path);
  };

  const handleHide2fa = () => {
    setShow2fa(false);
    router.asPath.includes('store')
      ? router.replace({
          pathname: router.asPath.split('?')[0],
          query: { id: router.query.id, showModal: true },
        })
      : router.replace(router.pathname, undefined, { shallow: true });
  };

  const [isOpen, setIsOpen] = useState(false);
  const showNavigation =
    status === authStatuses.SIGNED_IN || router.asPath.includes('/docs');

  const pageStyle = {
    width: '100%',
    maxWidth: '1320px',
    margin: '0 auto',
  };

  return (
    <div>
      {showNavigation && (
        <AppBar
          position="fixed"
          elevation={1}
          sx={{
            zIndex: 3,
            backgroundColor: colors.surfaces.layerThree,
            padding: '16px 24px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            borderRadius: '0px',
          }}
        >
          <Box sx={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <FontAwesomeIcon
              icon={faBars}
              width={20}
              size="xl"
              color={colors.primary.main}
              onClick={() => setIsOpen(!isOpen)}
              style={{ cursor: 'pointer' }}
              aria-label="Menu"
            />
            <Typography variant="h3">{config?.brand?.brandName} HQ</Typography>
          </Box>

          {user?.accessToken ? (
            <Link href={`${routes.member.path}/${memberData?.id}`} passHref>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <FontAwesomeIcon
                  icon={faUser}
                  width={14}
                  color={colors.grey[700]}
                  onClick={() => setIsOpen(!isOpen)}
                  style={{ cursor: 'pointer' }}
                  aria-label="Menu"
                />
                <Typography variant="h5" sx={{ cursor: 'pointer' }}>
                  {memberData?.fullName}
                </Typography>
              </Box>
            </Link>
          ) : null}
        </AppBar>
      )}
      {showNavigation && (
        <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} isLoggedIn={!!user?.accessToken} />
      )}
      <main className={showNavigation ? styles.appBodyAuthenticated : styles.appBody}>
        {!permission ? (
          showNavigation ? (
            <Box sx={pageStyle}>{children}</Box>
          ) : (
            children
          )
        ) : hasPermission ? (
          <Box sx={pageStyle}>{children}</Box>
        ) : (
          <Box sx={{ paddingTop: '24px' }}>
            <PermissionPlaceholder
              name={permission?.name}
              permission={permission?.permission}
            />
          </Box>
        )}
      </main>
      <Dialog open={isIdleModalShowing} onClose={handleSessionModalClose}>
        <ModalBody
          showClose
          handleClose={handleSessionModalClose}
          header="Session Expired"
          body="Your session has expired due to inactivity. Please log in to continue."
          severity="info"
        />
      </Dialog>
      {show2fa ? (
        <TwoFactorModal handleCloseModal={handleHide2fa} handleContinue={handleEnroll} />
      ) : null}
    </div>
  );
}
